/**
 * Bottom sheet.
 *
 * È il modo in cui il prodotto rispetta la regola "poco per schermata": il
 * dettaglio non affolla la vista, si apre da sotto quando lo chiedi e si
 * richiude con un gesto. Su desktop diventa un pannello centrato.
 */

import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "./primitives.tsx";

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  lato = "basso",
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Da dove entra: dal basso (il foglio di sempre) o da sinistra, un
   * cassetto a tutta altezza come quello delle conversazioni nelle app di
   * chat. Su schermo largo il cassetto resta un cassetto: è pensato per il
   * telefono e da PC non si apre mai.
   */
  lato?: "basso" | "sinistra";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Nel `body`, come le finestre: il pannello «Altro» stava dentro #root e
  // finiva **sotto** la finestra di Immobili o Oggi, aperta da lì (9/10/2026).
  // E sopra di loro, con lo z-index: da una schermata si deve poter arrivare
  // a un'altra, quindi il foglio vince sempre su quello che c'è sotto.
  return createPortal(
    <div
      className={cx(
        "fixed inset-0 z-[60] flex",
        lato === "sinistra"
          ? "items-stretch justify-start"
          : "items-end justify-center sm:items-center",
      )}
    >
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          "relative flex flex-col bg-surface shadow-lift",
          lato === "sinistra"
            ? "animate-cassetto h-full w-[84vw] max-w-sm rounded-r-card-lg"
            : "animate-sheet max-h-[88vh] w-full rounded-t-card-lg sm:rounded-card-lg sm:max-w-lg sm:mx-4",
        )}
      >
        {/* La maniglia: dice "questo si trascina" senza scriverlo. */}
        {lato === "basso" ? (
          <div className="flex justify-center pt-2.5 sm:hidden">
            <span className="h-1 w-9 rounded-pill bg-line-strong" />
          </div>
        ) : null}

        {title ? (
          <header
            className={cx(
              "flex items-center justify-between gap-3 px-5 pb-3 pt-4",
              lato === "sinistra" && "pt-[max(1rem,env(safe-area-inset-top))]",
            )}
          >
            <h2 className="text-[17px] font-bold text-ink">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="grid size-8 place-items-center rounded-pill text-ink-faint transition-colors hover:bg-surface-muted hover:text-ink"
            >
              <X size={17} />
            </button>
          </header>
        ) : null}

        <div className="scroll-soft flex-1 overflow-y-auto px-5 pb-5">
          {children}
        </div>

        {footer ? (
          <footer className="border-t border-line/70 px-5 py-4 installata:pb-[max(1rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        ) : (
          <div className="installata:pb-[env(safe-area-inset-bottom)]" />
        )}
      </div>
    </div>,
    document.body,
  );
}
